import { onMounted, onUnmounted } from 'vue'
import { useFCM } from 'src/composables/fcm/useFCM'
import { log } from 'src/services/app/log.service'
import { FCM_MESSAGE_TYPES, NOTIFICATION_TYPES } from 'src/services/consts/api.const'

const LEGACY_FCM_TYPES = new Set([...Object.values(NOTIFICATION_TYPES), 'notification'])

/**
 * Handles new FCM message types only. Call once in the app root (e.g. AppIndex).
 * Existing types stay in useFirebaseNotifications.
 */
export function useFcmUpdates() {
  const { subscribeToMessages, status } = useFCM()

  let unsubscribeMessages = null
  let unsubscribeBackgroundMessages = null

  onMounted(() => {
    unsubscribeMessages = subscribeToMessages(handleForegroundMessage)
    unsubscribeBackgroundMessages = setupBackgroundMessageListener()
  })

  onUnmounted(() => {
    if (unsubscribeMessages) {
      unsubscribeMessages()
    }
    if (unsubscribeBackgroundMessages) {
      unsubscribeBackgroundMessages()
    }
  })

  function handleForegroundMessage(payload) {
    const data = getPayloadData(payload)
    handleFCMUpdate(payload, data)
  }

  function setupBackgroundMessageListener() {
    if (!('serviceWorker' in navigator)) {
      return null
    }

    const messageHandler = (event) => {
      if (event.data?.type !== FCM_MESSAGE_TYPES.BACKGROUND) return

      const data = getPayloadData(event.data.payload)

      handleFCMUpdate(event.data.payload, data)
    }

    navigator.serviceWorker.addEventListener('message', messageHandler)
    log.info('[FCM] New-type background message listener registered')

    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler)
      log.info('[FCM] New-type background message listener removed')
    }
  }

  function handleFCMUpdate(payload, data) {
    if (!data) {
      log.warn('[FCM] Update skipped — message has no data:', payload)
      return
    }

    if (LEGACY_FCM_TYPES.has(data.type)) {
      return
    }

    log.info('[FCM] New update type received:', data.type, payload)

    switch (data.type) {
      // Add handlers for FCM_UPDATE_TYPES entries as backend ships new events
      default:
        log.warn('[FCM] Unhandled new update type:', data.type)
    }
  }

  return {
    fcmStatus: status,
  }
}

function getPayloadData(payload) {
  return (
    payload?.data || (payload?.notification && { type: 'notification', ...payload.notification })
  )
}
